import { useCallback, useState, type FormEvent, type ReactElement } from "react";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { space } from "@tokens/index";
import { Badge, Button, ConfirmDialog, IconButton, Text, TextInput } from "@primitives/index";
import { AdminSection, Field, FormActions, MutationError, RowActions } from "../admin-ui";
import type { RowAction } from "../admin-ui";
import { useDeleteManagementGroup, useRenameManagementGroup } from "../management-groups";
import type { ManagementGroup } from "../management-groups";
import { cancelOnEscape } from "./escapeCancels";

/**
 * Detail header: name, system badge, created line, rename and delete.
 * On `compact` the caller passes `onBack`, which renders the back control
 * that returns to the list.
 */

interface ManagementGroupHeaderProps {
    readonly group: ManagementGroup;
    readonly onBack?: () => void;
}

export function ManagementGroupHeader({ group, onBack }: ManagementGroupHeaderProps): ReactElement {
    const rename = useRenameManagementGroup();
    const remove = useDeleteManagementGroup();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(group.name);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const handleRename = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const trimmed = draft.trim();
            if (trimmed.length === 0) return;
            rename.mutate({ id: group.id, name: trimmed }, { onSuccess: () => setEditing(false) });
        },
        [rename, group.id, draft],
    );

    const cancelRename = useCallback(() => {
        if (rename.isPending) return;
        setEditing(false);
        setDraft(group.name);
    }, [rename.isPending, group.name]);

    const confirmDelete = useCallback(() => {
        setConfirmingDelete(false);
        remove.mutate(group.id);
    }, [remove, group.id]);

    const actions: readonly RowAction[] = [
        {
            key: "rename",
            label: "Rename",
            icon: Pencil,
            onSelect: () => {
                setDraft(group.name);
                setEditing(true);
            },
            disabled: group.isSystem,
        },
        {
            key: "delete",
            label: "Delete",
            icon: Trash2,
            onSelect: () => setConfirmingDelete(true),
            disabled: group.isSystem || remove.isPending,
            tone: "danger",
        },
    ];

    return (
        <AdminSection>
            <div style={{ display: "flex", alignItems: "flex-start", gap: space[3], minWidth: 0 }}>
                {onBack !== undefined && (
                    <IconButton
                        icon={ArrowLeft}
                        aria-label="Back to groups"
                        tooltip="Back to groups"
                        onClick={onBack}
                    />
                )}
                <div style={{ display: "grid", gap: space[3], flex: 1, minWidth: 0 }}>
                    {editing ? (
                        <form
                            onSubmit={handleRename}
                            onKeyDown={cancelOnEscape(cancelRename)}
                            style={{ display: "grid", gap: space[3] }}
                        >
                            <Field label="Group name">
                                {(id) => (
                                    <TextInput
                                        id={id}
                                        autoFocus
                                        fullWidth
                                        value={draft}
                                        disabled={rename.isPending}
                                        onChange={(e) => setDraft(e.target.value)}
                                    />
                                )}
                            </Field>
                            <FormActions
                                primary={
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        size="md"
                                        disabled={draft.trim().length === 0}
                                        busy={rename.isPending}
                                    >
                                        Save
                                    </Button>
                                }
                                secondary={
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="md"
                                        onClick={cancelRename}
                                        disabled={rename.isPending}
                                    >
                                        Cancel
                                    </Button>
                                }
                            />
                        </form>
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: space[3],
                                minWidth: 0,
                            }}
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gap: space[1],
                                    minWidth: 0,
                                    flex: "1 1 200px",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: space[2],
                                        minWidth: 0,
                                    }}
                                >
                                    <Text as="h2" role="title" style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                                        {group.name}
                                    </Text>
                                    {group.isSystem && (
                                        <Badge variant="neutral" size="sm">
                                            SYSTEM
                                        </Badge>
                                    )}
                                </div>
                                <Text role="caption" tone="muted" truncate>
                                    Created {new Date(group.createdAt).toLocaleString()} by{" "}
                                    <Text role="mono" size="xs" tone="muted">
                                        {group.createdBy}
                                    </Text>
                                </Text>
                            </div>
                            <RowActions actions={actions} menuLabel={`Actions for ${group.name}`} />
                        </div>
                    )}
                    <MutationError error={rename.error} />
                    <MutationError error={remove.error} />
                </div>
            </div>
            <ConfirmDialog
                open={confirmingDelete}
                title="Delete management group"
                message={`Delete '${group.name}'? This soft-deletes the group.`}
                confirmLabel="Delete"
                tone="danger"
                onConfirm={confirmDelete}
                onCancel={() => setConfirmingDelete(false)}
            />
        </AdminSection>
    );
}
